import { HttpInterceptorFn } from '@angular/common/http';

/**
 * Genera un X-Request-Id único por cada HTTP request.
 * Este header es el hilo conductor que permite rastrear una transacción
 * end-to-end desde el wapp → BFF → micros.
 *
 * - Si el request ya trae X-Request-Id, se respeta (no lo pisa).
 * - Si no lo trae, genera uno con crypto.randomUUID() (con fallback defensivo).
 * - Se aplica a TODOS los requests, incluso login.
 *
 * Debe registrarse PRIMERO en withInterceptors([...]) para asegurar que
 * el id exista antes de que otros interceptores corran.
 */
export const requestIdInterceptor: HttpInterceptorFn = (req, next) => {
  // Si el request ya trae X-Request-Id, respétalo
  if (req.headers.has('X-Request-Id')) {
    return next(req);
  }

  // Genera un nuevo id
  const requestId = generateRequestId();

  // Clona el request e inyecta el header
  return next(req.clone({ headers: req.headers.set('X-Request-Id', requestId) }));
};

/**
 * Genera un UUID aleatorio, con fallback defensivo para navegadores
 * que no tengan crypto.randomUUID() disponible.
 */
function generateRequestId(): string {
  // Intenta usar crypto.randomUUID (nativo en navegadores modernos)
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  // Fallback defensivo: genera un UUID v4 manualmente
  return fallbackUuid();
}

/**
 * Fallback defensivo para entornos sin crypto.randomUUID
 * (ej. contextos inseguros, navegadores muy antiguos).
 */
function fallbackUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
